import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Chip
} from '@material-ui/core';
import { ColorPicker } from 'material-ui-color';
import { Close as CloseIcon } from '@material-ui/icons';
import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import toastUtils from '../../utils/toastUtils';
import toastError from '../../errors/toastError';

const KanbanLaneConfigModal = ({ open, onClose, laneId, laneTitle, laneColor, onLaneUpdated }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#757575');
  const [loading, setLoading] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  useEffect(() => {
    if (open) {
      setName(laneTitle || '');
      setColor(laneColor || '#757575');
      setIsDefault(laneId === 'lane0');
    }
  }, [open, laneTitle, laneColor, laneId]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toastUtils.error(i18n.t('kanban.laneConfig.nameRequired'), 'Nome Obrigatório');
      return;
    }

    if (isDefault) {
      toastUtils.info(i18n.t('kanban.laneConfig.cannotEditDefault'), 'Faixa Padrão');
      return;
    }

    // Validate laneId is numeric
    const parsedLaneId = parseInt(laneId);
    if (isNaN(parsedLaneId) || parsedLaneId <= 0) {
      toastUtils.error('ID da faixa inválido', 'Erro de Validação');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/tags/${parsedLaneId}`, {
        name: name.trim(),
        color: color,
        kanban: 1
      });

      toastUtils.success(i18n.t('kanban.laneConfig.updated'), 'Faixa Atualizada');
      onLaneUpdated && onLaneUpdated();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setColor('#757575');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {i18n.t('kanban.laneConfig.title')}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {isDefault && (
            <Box mb={2}>
              <Chip 
                label={i18n.t('kanban.laneConfig.defaultLane')} 
                color="primary" 
                size="small" 
              />
              <Typography variant="body2" color="textSecondary" mt={1}>
                {i18n.t('kanban.laneConfig.defaultLaneDescription')}
              </Typography>
            </Box>
          )}
          
          <TextField
            label={i18n.t('kanban.laneConfig.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            variant="outlined"
            disabled={isDefault}
          />
          
          <Box>
            <Typography variant="body2" gutterBottom>
              {i18n.t('kanban.laneConfig.color')}
            </Typography>
            <ColorPicker
              value={color}
              onChange={(value) => setColor(`#${value.hex}`)}
              disabled={isDefault}
            />
          </Box>
          
          <Box display="flex" justifyContent="center" mt={2}>
            <Box
              width={200}
              height={60}
              borderRadius="8px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{ 
                background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                color: 'white',
                fontWeight: 500
              }}
            >
              {name || i18n.t('kanban.laneConfig.preview')}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          {i18n.t('kanban.laneConfig.cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained"
          disabled={loading || isDefault || !name.trim()}
        >
          {loading ? i18n.t('kanban.laneConfig.saving') : i18n.t('kanban.laneConfig.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KanbanLaneConfigModal;